import { Component, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Observable, map, of } from 'rxjs';
import {
  Filters,
  FnCaricamentoDati,
  SortBy,
} from 'src/app/components/lista-tabellare/classes/lista-tabellare-data-source';
import {
  BottoniListaEvent,
  Colonna,
  Filtri,
  ListaTabellareComponent,
} from 'src/app/components/lista-tabellare/lista-tabellare.component';
import { SpinnerOverlayComponent } from 'src/app/components/spinner-overlay/spinner-overlay.component';
import { AutovalutazioniService } from 'src/app/services/autovalutazioni/autovalutazioni.service';
import { SettoriService } from 'src/app/services/settori/settori.service';
import { UtenteService } from 'src/app/services/utente/utente.service';
import { UtilityService } from 'src/app/services/utility/utility.service';
import { DialogAnteprimaComponent } from './dialog/dialog-anteprima/dialog-anteprima.component';
import { DialogCreaCfgAutovalutazioneComponent } from './dialog/dialog-crea-cfg-autovalutazione/dialog-crea-cfg-autovalutazione.component';

@Component({
  selector: 'app-cfg-autovalutazioni',
  templateUrl: './cfg-autovalutazioni.component.html',
  styleUrls: ['./cfg-autovalutazioni.component.scss'],
  standalone: false,
})
export class CfgAutovalutazioniComponent {
  @ViewChild(SpinnerOverlayComponent) spinner!: SpinnerOverlayComponent;
  @ViewChild('tabella') tabella!: ListaTabellareComponent;

  public filtri: Filtri[] = [
    {
      forControlName: 'titolo',
      input: 'text',
      titolo: 'Titolo',
    },
    {
      forControlName: 'tipo',
      input: 'multiple-option',
      titolo: 'Tipo',
      fnDatiOption: () =>
        of([
          {
            valore: 'GENERICO',
            desc: 'Generico',
          },
          {
            valore: 'SETTORE',
            desc: 'Per settore',
          },
        ]),
      descValueOption: 'desc',
      idValueOption: 'valore',
    },
    {
      forControlName: 'stato',
      input: 'multiple-option',
      titolo: 'Stato',
      fnDatiOption: () =>
        of([
          {
            valore: 'ARCHIVIATO',
            desc: 'Archiviato',
          },
          {
            valore: 'PUBBLICATO',
            desc: 'Pubblicato',
          },
          {
            valore: 'BOZZA',
            desc: 'Bozza',
          },
        ]),
      descValueOption: 'desc',
      idValueOption: 'valore',
    },
    {
      forControlName: 'settore',
      input: 'multiple-option',
      titolo: 'Settore',
      fnDatiOption: () =>
        this.settoriService.getSettori(0, 1000).pipe(map((value) => value?.content || [])),
      descValueOption: 'titolo',
      idValueOption: 'id',
      riempi: true,
    },
  ];

  public colonne: { [key: string]: Colonna } = {
    titolo: {
      title: 'Titolo',
      value: 'titolo',
      sortable: true,
    },
    tipo: {
      title: 'Tipo',
      sortable: true,
      campiSort: ['tipo'],
      value: (record: any) =>
        record.tipoAutovalutazione === 'SETTORE' ? 'Per settore' : 'Sito vetrina',
    },
    specific: {
      title: 'Specific',
      sortable: true,
      campiSort: ['specificUser'],
      value: (record: any) => (record.specificUser ? 'SI' : 'NO'),
    },
    settori: {
      title: 'Settori',
      sortable: true,
      campiSort: ['settore'],
      value: (record: any) => record?.settori?.map((set: any) => set.titolo),
    },

    stato: this.utilityService.getColonnaStato(),

    modifica: {
      type: 'button',
      buttonIcon: (record: any) => (record?.stato === 'BOZZA' ? 'mode_edit' : 'visibility'),
      title: (record: any) => (record?.stato === 'BOZZA' ? 'Modifica' : 'Visualizza'),
      buttonId: (record: any) => (record?.stato === 'BOZZA' ? 'modifica' : 'visualizza'),
      buttonMostraSempre: true,
    },
    duplica: {
      type: 'button',
      buttonIcon: 'content_copy',
      title: 'Duplica',
      buttonId: 'duplica',
    },
    elimina: {
      type: 'button',
      buttonIcon: (record: any) => (record.stato === 'BOZZA' ? 'delete' : 'inventory_2'),
      title: (record: any) => (record?.stato === 'BOZZA' ? 'Elimina' : 'Archivia'),
      buttonId: 'elimina',
      nascondiButton: (record: any) => record?.stato === 'ARCHIVIATO',
    },
  };

  constructor(
    private readonly autovalutazioniService: AutovalutazioniService,
    private readonly dialog: MatDialog,
    private readonly utilityService: UtilityService,
    private readonly settoriService: SettoriService,
    public utenteService: UtenteService,
  ) {}

  public crea(recordCfgAuto?: any) {
    this.spinner.show();

    let obs: Observable<any | null>;

    if (recordCfgAuto) {
      obs = this.autovalutazioniService.getCfgAutovalutazioneById(recordCfgAuto.id);
    } else {
      obs = of(null);
    }

    obs.subscribe({
      next: (autovalutazione) => {
        this.spinner.hide();

        if (recordCfgAuto?.stato === 'PUBBLICATO') {
          this.utilityService.openDialog({
            titolo: 'Attenzione',
            descrizione: 'Assessment di Sostenibilità pubblicata non è possibile modificarla',
            bottoni: [{ nome_btn: 'Ok' }],
          });
          return;
        }

        const dialogFiltro = this.dialog.open(DialogCreaCfgAutovalutazioneComponent, {
          data: {
            cfgAutovalutazione: autovalutazione,
          },
          panelClass: ['dialog-container'],
          disableClose: false,
          width: '80%',
          height: '95%',
          autoFocus: false,
        });

        dialogFiltro.afterClosed().subscribe((result) => {
          this.tabella.caricaDati();
        });
      },
      error: (error) => {
        console.error(error);

        this.spinner.hide();

        this.utilityService.openDialog({
          titolo: 'Attenzione',
          descrizione: error.error.message,
          bottoni: [{ nome_btn: 'Ok' }],
        });
      },
    });
  }

  public visualizza(autovalutazione: any) {
    const dialogFiltro = this.dialog.open(DialogAnteprimaComponent, {
      data: {
        ambito: autovalutazione.ambiti,
      },
      panelClass: ['dialog-container'],
      disableClose: false,
      width: '80%',
      height: '95%',
      autoFocus: false,
    });

    dialogFiltro.afterClosed().subscribe((result) => {});
  }

  public fnCaricamentoDati: FnCaricamentoDati = (
    page: number,
    pageSize: number,
    ricerca?: string,
    filters?: Filters[],
    sortBy?: SortBy[],
  ) => {
    return this.autovalutazioniService.getCfgAutovalutazioni(
      page,
      pageSize,
      ricerca,
      filters,
      sortBy,
    );
  };

  public bottoniListaClick(event: BottoniListaEvent) {
    switch (event.id) {
      case 'modifica':
        this.crea(event.data);
        break;
      case 'visualizza':
        this.visualizza(event.data);
        break;
      case 'elimina':
        this._delCfgAutovalutazione(event.data);
        break;
      case 'duplica':
        this._duplica(event.data);
        break;
      default:
        console.warn('ATTENZIONE: bottone ' + event.id + ' non riconosciuto');
    }
  }
  private _duplica(cfgAutovalutazione: any) {
    this.utilityService.openDialog({
      titolo: 'Attenzione',
      descrizione: 'Duplicare il record selezionato?',
      bottoni: [
        {
          nome_btn: 'No',
        },
        {
          nome_btn: 'Si',
          handler: () => {
            this.autovalutazioniService.duplicaAutovalutazione(cfgAutovalutazione.id).subscribe({
              next: (result: any) => {
                this.utilityService.openSnackBar(' Autovalutazione duplicata ', '', {
                  duration: 2000,
                  panelClass: ['success-snackbar'],
                });
                this.tabella.caricaDati();
              },
              error: (err: any) => {
                console.error('Errore :', err);

                this.utilityService.openDialog({
                  titolo: 'Attenzione',
                  descrizione: 'Errore duplicazione',
                  bottoni: [{ nome_btn: 'Ok' }],
                });
              },
            });
          },
        },
      ],
    });
  }

  private _delCfgAutovalutazione(cfgAutovalutazione: any) {
    /*     if (cfgAutovalutazione?.stato === 'PUBBLICATO') {
          this.utilityService.openDialog({
            titolo: 'Attenzione',
            descrizione: 'Autovalutazione pubblicata non è possibile cancellarla',
            bottoni: [{ nome_btn: 'Ok' }]
          })
          return;
        } */

    this.utilityService.openDialog({
      titolo: 'Attenzione',
      descrizione:
        cfgAutovalutazione?.stato === 'PUBBLICATO'
          ? "Confermi l'archiviazione dell'assessment?"
          : "Confermi l'eliminazione dell'assessment?",
      bottoni: [
        { nome_btn: 'NO' },
        {
          nome_btn: 'SI',
          handler: () => {
            this.spinner.show();
            // fai cancellazione
            this.autovalutazioniService.delCfgAutovalutazione(cfgAutovalutazione.id).subscribe({
              next: (result: any) => {
                this.utilityService.openSnackBar(
                  cfgAutovalutazione?.stato === 'PUBBLICATO'
                    ? 'Assessment di Sostenibilità archiviata'
                    : 'Assessment di Sostenibilità eliminata',
                  '',
                  {
                    duration: 2000,
                    panelClass: ['success-snackbar'],
                  },
                );
                this.tabella.caricaDati();
                this.spinner.hide();
              },
              error: (err: any) => {
                this.spinner.hide();
                console.error('Errore :', err);

                this.utilityService.openDialog({
                  titolo: 'Attenzione',
                  descrizione: 'Errore elimina',
                  bottoni: [{ nome_btn: 'Ok' }],
                });
              },
            });
          },
        },
      ],
    });
  }
}
